import { useState } from 'react';
import { useProspectingAvailability } from './useProspectingAvailability';
import { Button, InlineError, Select, Skeleton } from '../../components/ui';
import { useLinkedinScores, useScoreboardPeople } from './linkedinJourney';
import { CurrentMessageReports } from './CurrentMessageReports';
import { useCopySets } from './hooks';
import { copyMessageLabel } from './copyMessages';
import {
  manilaDay,
  messageRows,
  scoreboardCsv,
  scoreboardRows,
  SCORE_METRICS,
} from './linkedinScoreboard';
import type { ScoreMode, ScoreTier } from './linkedinScoreboard';

export function LinkedinScoreboard({
  onDownload,
}: {
  onDownload: (filename: string, csv: string) => void;
}) {
  const scores = useLinkedinScores();
  const { reader_enabled, message_enabled } = useProspectingAvailability();
  const names = useScoreboardPeople();
  const copies = useCopySets();
  const [tier, setTier] = useState<ScoreTier>('tier2');
  const [mode, setMode] = useState<ScoreMode>('weeks');
  const { weeks, people } = scoreboardRows(scores.data?.weekly ?? [], tier, names.data);
  const messages = messageRows(
    (scores.data?.messages ?? []).map((row) => {
      const copy = copies.data?.find((c) => c.id === row.copy_set_id);
      return !message_enabled || copy === undefined
        ? row
        : { ...row, copy_set_name: copyMessageLabel(copy) };
    }),
    tier,
  );
  const metricRows = mode === 'weeks' ? weeks : people;
  const count = mode === 'messages' ? messages.length : metricRows.length;
  return (
    <section
      className="tracker-dashboard-card linkedin-scoreboard"
      aria-label="LinkedIn scoreboard"
    >
      <h2 className="tracker-dashboard-card-title">LinkedIn scoreboard</h2>
      <p className="tracker-field-note">
        Steps counted in the Manila week they happened. By person means who logged the step, or the
        deal owner for Won. This report uses its own tier filter.
      </p>
      {reader_enabled && mode === 'messages' && (
        <p className="tracker-field-note">
          Legacy milestone attribution. The current-selection reports below are separate and
          preserve original credit in history.
        </p>
      )}
      <div className="linkedin-scoreboard-controls">
        <Select
          label="Scoreboard tier"
          value={tier}
          onChange={(e) => setTier(e.target.value as ScoreTier)}
          options={[
            { value: 'tier2', label: 'Tier 2' },
            { value: 'tier1', label: 'Tier 1' },
            { value: 'trash', label: 'Trash' },
            { value: 'all', label: 'All' },
          ]}
        />
        {(
          [
            ['weeks', 'By week'],
            ['messages', 'By message'],
            ['people', 'By person'],
          ] as const
        ).map(([value, label]) => (
          <Button
            key={value}
            size="sm"
            variant={mode === value ? 'primary' : 'secondary'}
            aria-pressed={mode === value}
            onClick={() => setMode(value)}
          >
            {label}
          </Button>
        ))}
        <Button
          size="sm"
          variant="secondary"
          disabled={scores.isPending || scores.isError}
          onClick={() =>
            onDownload(
              `linkedin-scoreboard-${manilaDay()}.csv`,
              scoreboardCsv(mode, weeks, messages, people),
            )
          }
        >
          Download scoreboard CSV
        </Button>
      </div>
      {scores.isPending ? (
        <Skeleton lines={3} label="Loading LinkedIn scoreboard" />
      ) : scores.isError ? (
        <InlineError>The LinkedIn scoreboard could not be loaded. Try refreshing.</InlineError>
      ) : (
        <>
          {names.isError && mode === 'people' && (
            <InlineError>People's names could not be loaded. Their IDs are shown.</InlineError>
          )}
          {count === 0 ? (
            <p className="tracker-field-note">No LinkedIn activity for this tier yet.</p>
          ) : (
            <div className="tracker-table-scroll tracker-table-scroll--short">
              <table className="tracker-perf-table" aria-label={`LinkedIn scoreboard ${mode}`}>
                <thead>
                  <tr>
                    {mode === 'messages' ? (
                      [
                        'Message',
                        'People messaged',
                        'Replies',
                        'Reply %',
                        'Info replies',
                        'Info %',
                        'Calls booked',
                      ].map((label) => (
                        <th key={label} scope="col">
                          {label}
                        </th>
                      ))
                    ) : (
                      <>
                        <th scope="col">{mode === 'weeks' ? 'Week' : 'Person'}</th>
                        {SCORE_METRICS.map(([key, label]) => (
                          <th key={key} scope="col">
                            {label}
                          </th>
                        ))}
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {mode === 'messages'
                    ? messages.map((row) => (
                        <tr key={row.copy_set_id}>
                          <th scope="row">{row.label}</th>
                          {[
                            row.people_messaged,
                            row.replied,
                            row.replyPercent,
                            row.info_replied,
                            row.infoPercent,
                            row.calls_booked,
                          ].map((value, i) => (
                            <td key={i}>{value}</td>
                          ))}
                        </tr>
                      ))
                    : metricRows.map((row) => (
                        <tr key={row.key}>
                          <th scope="row">{row.label}</th>
                          {SCORE_METRICS.map(([key]) => (
                            <td key={key}>{row[key]}</td>
                          ))}
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
      <CurrentMessageReports tier={tier} />
    </section>
  );
}
